import { useMemo, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'

import { Box, Card, RadioButton, Text } from '@factorialco/deprecated-design-system'
import { F0Alert, F0Button, F0Dialog } from '@factorialco/f0-react'
import {
  CardSelectableContainer,
  CardSelectableItem,
} from '@factorialco/f0-react/dist/experimental'
import { Features } from 'generated/featureDefinitions'
import {
  BulkCreateAuthenticationInvitationDocument,
  CreateDatevLaudsEmployeeImportDocument,
  EmployeeCollectionListEmployeeFragment,
  EmployeeCollectionListIdsEmployeeFragment,
  EmployeeCollectionPageDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { BecomeModal } from 'modules/authentication/public'
import EmployeeCollection from 'modules/employees/components/EmployeeCollection'
import BulkActivateModal from 'modules/employees/components/EmployeeCollection/BulkActivateModal'
import BulkFDContractsModal from 'modules/employees/components/EmployeeCollection/BulkFDContractsModal'
import BulkPauseModal from 'modules/employees/components/EmployeeCollection/BulkPauseModal'
import BulkTerminateModal from 'modules/employees/components/EmployeeCollection/BulkTerminateModal'
import Header from 'modules/employees/components/EmployeeCollection/Header'
import TerminationImporterModal from 'modules/employees/components/EmployeeCollection/TerminationImporterModal'
import { useUnterminateEmployee } from 'modules/employees/hooks/useUnterminateEmployee'
import BulkCreateAdditionalCompensationsWizardModal from 'modules/employees/pages/BulkCreateAdditionalCompensationsPage/BulkCreateAdditionalCompensationsWizardModal'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import integrationCodes from 'modules/marketplace/lib/integrationCodes'
import {
  UpdaterLeModeModal,
  UpdaterLeModeResult,
} from 'modules/universal_importer/components/UpdaterLeModeModal'
import { UnifiedEditionFlow } from 'modules/employees/components/UnifiedEditionFlow'
import type { UnifiedEditionModalResult } from 'modules/universal_importer/components/UnifiedEditionModal'

const CUSTOM_TABLES_FLAG_ENABLERS = [
  'employees_custom_tables',
  'trainings_custom_table',
  'competencies_custom_table',
  'growth_plans_custom_table',
]

const EmployeeCollectionPage = () => {
  const { pathname } = useLocation()
  const { access, company } = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { unterminate } = useUnterminateEmployee()
  const [showLegalEntitySelectorModal, setShowLegalEntitySelectorModal] = useState(false)
  const [showEditContractsModeModal, setShowEditContractsModeModal] = useState(false)
  const [selectedEditContractsMode, setSelectedEditContractsMode] = useState<
    'all' | 'current' | 'new'
  >('current')
  const [selectedEmployees, setSelectedEmployees] = useState<
    EmployeeCollectionListIdsEmployeeFragment[]
  >([])
  const [importType, setImportType] = useState<string | null>(null)
  const [modalAction, setModalAction] = useState<string>('import')
  const [showBecomeModal, setShowBecomeModal] = useState(false)
  const [employeeToBecome, setEmployeeToBecome] =
    useState<EmployeeCollectionListEmployeeFragment | null>(null)
  const [showTerminateConfirm, setShowTerminateConfirm] = useState(false)
  const [selectedForTermination, setSelectedForTermination] = useState<string[]>([])
  const [eligibleForTermination, setEligibleForTermination] = useState<string[]>([])
  const [terminatedSelectedCount, setTerminatedSelectedCount] = useState<number>(0)
  const [showPauseConfirm, setShowPauseConfirm] = useState(false)
  const [pauseSelectedCount, setPauseSelectedCount] = useState<number>(0)
  const [pauseEligibleIds, setPauseEligibleIds] = useState<string[]>([])
  const [showActivateConfirm, setShowActivateConfirm] = useState(false)
  const [showFDContractsConfirm, setShowFDContractsConfirm] = useState(false)
  const [activateSelectedCount, setActivateSelectedCount] = useState<number>(0)
  const [activateEligibleIds, setActivateEligibleIds] = useState<string[]>([])
  const [showTerminationImporterModal, setShowTerminationImporterModal] = useState(false)
  const [showUpdaterLeModeModal, setShowUpdaterLeModeModal] = useState(false)
  const [showUnifiedEditionFlow, setShowUnifiedEditionFlow] = useState(false)
  const [unifiedEditionEmployees, setUnifiedEditionEmployees] = useState<
    Array<{ id: string; firstName: string; lastName: string; legalEntityId: string | null }>
  >([])
  const [unifiedEditionTarget, setUnifiedEditionTarget] = useState<'employees' | 'contracts'>(
    'employees'
  )
  const [unifiedEditionContractMode, setUnifiedEditionContractMode] = useState<'all' | 'current'>(
    'current'
  )
  const [additionalCompensationEmployeeIds, setAdditionalCompensationEmployeeIds] = useState<
    string[]
  >([])

  const sortedLegalEntities = [...(company.legalEntities || [])].sort((a, b) =>
    a.legalName.localeCompare(b.legalName)
  )

  const [selectedLegalEntity, setSelectedLegalEntity] = useState(
    sortedLegalEntities[0]?.id.toString() || ''
  )

  const legalEntityOptions = useMemo(() => {
    let entitiesToShow = sortedLegalEntities

    if (selectedEmployees.length > 0) {
      const selectedIds = new Set(
        selectedEmployees
          .map((employee) => employee.legalEntity?.id)
          .filter((id): id is string => id !== null && id !== undefined)
      )
      entitiesToShow = sortedLegalEntities.filter((le) => selectedIds.has(le.id))
    }

    return entitiesToShow.map((legalEntity) => ({
      label: legalEntity.legalName,
      value: legalEntity.id.toString(),
    }))
  }, [sortedLegalEntities, selectedEmployees])

  useUnsafeEffect(() => {
    if (showLegalEntitySelectorModal && legalEntityOptions.length > 0) {
      const currentOptionExists = legalEntityOptions.some(
        (option) => option.value === selectedLegalEntity
      )
      if (!currentOptionExists) {
        setSelectedLegalEntity(legalEntityOptions[0]?.value || '')
      }
    }
  }, [showLegalEntitySelectorModal, legalEntityOptions, selectedLegalEntity])

  const { data, loading, error } = useQuery(EmployeeCollectionPageDocument, {
    variables: {
      companyId: company.id,
      featureNames: [
        'legacy_job_catalog',
        'seasonal_contracts_disabled',
        'legacy_contracts_edition',
        'dev_universal_contracts_import',
        'dev_fixed_discontinuous_contracts',
        'dev_compensations_per_worked_day',
        'become_enabled',
        'dev_fulltext_employee_search',
        'dev_universal_updater_employees',
        'dev_universal_importer_field_selection',
        ...CUSTOM_TABLES_FLAG_ENABLERS,
      ],
      featureAvailabilities: [
        Features.timeoff_management,
        Features.compensations_management,
        Features.payroll_management,
        'contracts_discontinuous_contracts',
      ],
    },
  })
  const canCreateEmployee = data?.apiCore._policies.employeeCreate.can ?? false
  const canInvite = data?.employees._policies.inviteEmployee.can ?? false
  const canEditContracts = data?.contracts._policies.contractEditContractVersion.can ?? false
  const canEditEmployeesInBulk = data?.employees._policies.editEmployeesInBulkButton.can ?? false
  const canSeeFormerEmployees = data?.employees._policies.seeFormerEmployees.can ?? false
  const canSeeJobTitle = data?.contracts._policies.contractSeeJobTitle.can ?? false
  const canSeeJobCatalogTree = data?.jobCatalog._policies.jobCatalogTreeSee.can ?? false
  const canSeeJobCatalog = data?.jobCatalog._policies.seeJobCatalog.can ?? false
  const hasLegacyJobCatalog =
    data?.features.featuresConnection.nodes.find((feature) => feature.name === 'legacy_job_catalog')
      ?.enabled ?? false
  const canSeePendingPromotions =
    data?.contracts._policies.contractSeePendingPromotions?.can ?? false
  const canSeeStartDate = data?.contracts._policies.contractSeeStartDate.can ?? false
  const canSeeEndDate = data?.contracts._policies.contractSeeEndDate.can ?? false
  const hasLegacyContractStructure =
    data?.features.featuresConnection.nodes.find(
      (feature) => feature.name === 'seasonal_contracts_disabled'
    )?.enabled ?? false
  const hasDevFixedDiscontinuousContractsFeature =
    data?.companies.featureAvailabilitiesConnection.nodes.some(
      (featureAvailability) =>
        featureAvailability.id === 'contracts_discontinuous_contracts' &&
        featureAvailability.available &&
        !hasLegacyContractStructure
    ) ?? false
  const canImportEmployees = data?.onboardingImporter._policies.importEmployees.can ?? false
  const canExportEmployees = data?.employees._policies.exportEmployees.can ?? false
  const canExportTimeoff = data?.apiCore._policies.accessDownloadLeaves.can ?? false

  const hasCustomTablesFeature = CUSTOM_TABLES_FLAG_ENABLERS.some(
    (flag) =>
      data?.features.featuresConnection.nodes.find((feature) => feature.name === flag)?.enabled
  )
  const canBulkImportCustomTables =
    data?.apiCore._policies.customTablesBulkImport.can && hasCustomTablesFeature
  const hasBecomeEnabledFeature =
    data?.features.featuresConnection.nodes.find((feature) => feature.name === 'become_enabled')
      ?.enabled ?? false
  const hasDatevIntegration =
    data?.marketplace.companyInstallationsConnection.nodes.some(
      (installation) => installation.integrationDatoId === integrationCodes.datev_lauds_factorial
    ) ?? false
  const hasDevUniversalContractsEditionFeature = !(
    data?.features.featuresConnection.nodes.find(
      (feature) => feature.name === 'legacy_contracts_edition'
    )?.enabled ?? false
  )
  const hasDevUniversalContractsImportFeature =
    data?.features.featuresConnection.nodes.find(
      (feature) => feature.name === 'dev_universal_contracts_import'
    )?.enabled ?? false
  const canImportTimeoffAllowances =
    data?.companies.featureAvailabilitiesConnection.nodes.find(
      (featureAvailability) => featureAvailability.id === Features.timeoff_management
    )?.available ?? false
  const hasCompensationsManagementFeature =
    data?.companies.featureAvailabilitiesConnection.nodes.find(
      (featureAvailability) => featureAvailability.id === Features.compensations_management
    )?.available ?? false
  const hasPayrollManagementFeature =
    data?.companies.featureAvailabilitiesConnection.nodes.find(
      (featureAvailability) => featureAvailability.id === Features.payroll_management
    )?.available ?? false
  const hasDevUniversalUpdaterEmployeesFeature =
    data?.features.featuresConnection.nodes.find(
      (feature) => feature.name === 'dev_universal_updater_employees'
    )?.enabled ?? false
  const hasFulltextEmployeeSearch =
    data?.features.featuresConnection.nodes.find(
      (feature) => feature.name === 'dev_fulltext_employee_search'
    )?.enabled ?? false
  const canImportResources = data?.universalImporter._policies.importResources.can ?? false
  const hasDevCompensationsPerWorkedDayFeature =
    data?.features.featuresConnection.nodes.find(
      (feature) => feature.name === 'dev_compensations_per_worked_day'
    )?.enabled ?? false
  const hasFieldSelectionFeature =
    data?.features.featuresConnection.nodes.find(
      (feature) => feature.name === 'dev_universal_importer_field_selection'
    )?.enabled ?? false

  const handleBecome = (employee: EmployeeCollectionListEmployeeFragment) => {
    setEmployeeToBecome(employee)
    track(trackingEvents.employee.become.startBecome, { from: pathname })
    setShowBecomeModal(true)
  }

  const handleClickAdditionalCompensationsBulkEdition = (
    selectedBulkEditEmployees: EmployeeCollectionListIdsEmployeeFragment[]
  ) => {
    const selectedEmployeesIds = selectedBulkEditEmployees.map((employee) => employee.id)

    if (hasDevCompensationsPerWorkedDayFeature) {
      setAdditionalCompensationEmployeeIds(selectedEmployeesIds)

      return
    }

    navigate(navigation.employees.additionalCompensations.create.fullPath(), {
      state: { employeeIds: selectedEmployeesIds },
    })
  }

  const handleClickSupplementsBulkEdition = (
    selectedBulkEditEmployees: EmployeeCollectionListIdsEmployeeFragment[]
  ) => {
    const selectedEmployeesIds = selectedBulkEditEmployees.map((employee) => employee.id)

    navigate(navigation.employees.bulkFixContracts.fullPath(), {
      state: {
        employeesIds: selectedEmployeesIds,
      },
    })
  }

  const handleClickEmployeesBulkEdition = (
    selectedBulkEditEmployees: EmployeeCollectionListIdsEmployeeFragment[]
  ) => {
    const selectedEmployeesIds = selectedBulkEditEmployees.map((employee) => employee.id)

    if (hasFieldSelectionFeature) {
      // New flow: show UnifiedEditionFlow modal in-page (no navigation)
      setUnifiedEditionEmployees(
        selectedBulkEditEmployees.map((emp) => ({
          id: emp.id,
          firstName: emp.firstName,
          lastName: emp.lastName,
          legalEntityId: emp.legalEntity?.id ?? null,
        }))
      )
      setUnifiedEditionTarget('employees')
      setShowUnifiedEditionFlow(true)
    } else {
      // Legacy flow: check multi-LE and show LE selector if needed
      const selectedLegalEntityIdsFromSelectedEmployees = Array.from(
        new Set(selectedBulkEditEmployees.map((employee) => employee.legalEntity?.id))
      )

      if (selectedLegalEntityIdsFromSelectedEmployees.length > 1) {
        setModalAction('edit')
        setSelectedEmployees(selectedBulkEditEmployees)
        setShowLegalEntitySelectorModal(true)
      } else {
        const selectedLegalEntityFromSelectedEmployees =
          selectedLegalEntityIdsFromSelectedEmployees[0]?.toString()

        if (selectedLegalEntityFromSelectedEmployees) {
          setSelectedLegalEntity(selectedLegalEntityFromSelectedEmployees)
          navigate(navigation.employees.edit.fullPath(), {
            state: {
              employeesIds: selectedEmployeesIds,
              selectedLegalEntity: selectedLegalEntityFromSelectedEmployees,
            },
          })
        }
      }
    }
  }

  const handleUnifiedEditionConfirm = (result: UnifiedEditionModalResult) => {
    setShowUnifiedEditionFlow(false)
    const selectedIds = new Set(result.selectedEmployeeIds)
    const employeesState = unifiedEditionEmployees.filter((e) => selectedIds.has(e.id))
    const employeesIds = employeesState.map((e) => e.id)

    if (unifiedEditionTarget === 'contracts') {
      navigate(navigation.employees.editContracts.fullPath(), {
        state: {
          employeesIds,
          employees: employeesState,
          contractMode: unifiedEditionContractMode,
          modalResult: result,
        },
      })
    } else {
      navigate(navigation.employees.edit.fullPath(), {
        state: {
          employeesIds,
          employees: employeesState,
          modalResult: result,
        },
      })
    }
  }

  const handleClickContractsBulkEdition = (
    selectedBulkEditEmployees: EmployeeCollectionListIdsEmployeeFragment[]
  ) => {
    if (hasFieldSelectionFeature) {
      // New flow: UnifiedEditionModal on EditContractsInBulkPage handles LE + field selection
      setModalAction('edit')
      setImportType('contracts')
      setSelectedEmployees(selectedBulkEditEmployees)
      setShowEditContractsModeModal(true)
    } else {
      // Legacy flow: check multi-LE and show LE selector if needed
      const selectedLegalEntityIdsFromSelectedEmployees = Array.from(
        new Set(selectedBulkEditEmployees.map((employee) => employee.legalEntity?.id))
      )

      if (selectedLegalEntityIdsFromSelectedEmployees.length > 1) {
        setModalAction('edit')
        setImportType('contracts')
        setSelectedEmployees(selectedBulkEditEmployees)
        setShowLegalEntitySelectorModal(true)
      } else {
        const selectedLegalEntityFromSelectedEmployees =
          selectedLegalEntityIdsFromSelectedEmployees[0]?.toString()

        if (selectedLegalEntityFromSelectedEmployees) {
          setModalAction('edit')
          setImportType('contracts')
          setSelectedEmployees(selectedBulkEditEmployees)
          setSelectedLegalEntity(selectedLegalEntityFromSelectedEmployees)
          setShowEditContractsModeModal(true)
        }
      }
    }
  }

  const handleOnCloseModal = () => {
    setShowLegalEntitySelectorModal(false)
    setImportType(null)
    setModalAction('import')
    setSelectedEmployees([])
  }

  const handleOnClickNextModal = () => {
    setShowLegalEntitySelectorModal(false)

    if (modalAction === 'import') {
      if (importType === 'contracts') {
        navigateToImportAgreements(selectedLegalEntity)
        setImportType(null)
      } else {
        navigate(
          navigation.employees.importEmployees
            .fullPath({
              legal_entity_id: selectedLegalEntity,
            })
            .replace(/\*+$/, ''),
          {
            state: { selectedLegalEntity },
          }
        )
      }
    } else if (modalAction === 'edit' && importType === 'contracts') {
      setShowEditContractsModeModal(true)
    } else if (modalAction === 'edit') {
      // Legacy flow: navigate to edit page with selected LE and filtered employees
      const selectedEmployeesByLegalEntity = selectedEmployees.filter(
        (employee) => (employee.legalEntity?.id?.toString() ?? '0') === selectedLegalEntity
      )
      const filteredEmployeeIds = selectedEmployeesByLegalEntity.map((employee) => employee.id)

      navigate(navigation.employees.edit.fullPath(), {
        state: {
          employeesIds: filteredEmployeeIds,
          selectedLegalEntity,
        },
      })
    } else if (modalAction === 'update' && importType === 'employees') {
      navigate(
        navigation.employees.updaterEmployees
          .fullPath({
            legal_entity_id: selectedLegalEntity,
          })
          .replace(/\*+$/, ''),
        {
          state: { selectedLegalEntity },
        }
      )
    } else if (modalAction === 'update' && importType === 'contracts') {
      navigate(
        navigation.employees.updaterContracts
          .fullPath({
            legal_entity_id: selectedLegalEntity,
          })
          .replace(/\*+$/, ''),
        {
          state: { selectedLegalEntity },
        }
      )
    } else if (modalAction === 'new_version') {
      navigateToImportContracts(selectedLegalEntity, selectedEmployees)
    }
  }

  const handleRequestBulkTerminate = ({
    selectedEmployeeIds,
    eligibleEmployeeIds,
    terminatedSelectedCount: terminatedSelectedCountParam,
  }: {
    selectedEmployeeIds: string[]
    eligibleEmployeeIds: string[]
    terminatedSelectedCount: number
  }) => {
    setSelectedForTermination(selectedEmployeeIds)
    setEligibleForTermination(eligibleEmployeeIds)
    setTerminatedSelectedCount(terminatedSelectedCountParam)
    setShowTerminateConfirm(true)
  }

  const handleRequestBulkPause = ({
    selectedEmployees: _selectedEmployees,
    eligibleEmployees,
  }: {
    selectedEmployees: EmployeeCollectionListIdsEmployeeFragment[]
    eligibleEmployees: EmployeeCollectionListIdsEmployeeFragment[]
  }) => {
    setPauseSelectedCount(_selectedEmployees.length)
    setPauseEligibleIds(eligibleEmployees.map((e) => e.id))
    setShowPauseConfirm(true)
  }

  const handleRequestBulkActivate = ({
    selectedEmployees: _selectedEmployees,
    eligibleEmployees,
  }: {
    selectedEmployees: EmployeeCollectionListIdsEmployeeFragment[]
    eligibleEmployees: EmployeeCollectionListIdsEmployeeFragment[]
  }) => {
    setActivateSelectedCount(_selectedEmployees.length)
    setActivateEligibleIds(eligibleEmployees.map((e) => e.id))
    setShowActivateConfirm(true)
  }

  const onClickImportPeople = () => {
    setImportType('employees')
    if (sortedLegalEntities.length > 1) {
      setModalAction('import')
      setShowLegalEntitySelectorModal(true)
    } else {
      setSelectedLegalEntity(selectedLegalEntity)
      navigate(
        navigation.employees.importEmployees
          .fullPath({
            legal_entity_id: selectedLegalEntity,
          })
          .replace(/\*+$/, ''),
        {
          state: { selectedLegalEntity: selectedLegalEntity },
        }
      )
    }
  }

  const onClickEditPeopleFromFile = () => {
    setImportType('employees')
    if (hasFieldSelectionFeature) {
      setShowUpdaterLeModeModal(true)
    } else if (sortedLegalEntities.length > 1) {
      setModalAction('update')
      setShowLegalEntitySelectorModal(true)
    } else {
      setSelectedLegalEntity(selectedLegalEntity)
      navigate(
        navigation.employees.updaterEmployees
          .fullPath({
            legal_entity_id: selectedLegalEntity,
          })
          .replace(/\*+$/, ''),
        {
          state: { selectedLegalEntity: selectedLegalEntity },
        }
      )
    }
  }

  const onClickEditAgreementsFromFile = () => {
    setImportType('contracts')
    if (hasFieldSelectionFeature) {
      setShowUpdaterLeModeModal(true)
    } else if (sortedLegalEntities.length > 1) {
      setModalAction('update')
      setShowLegalEntitySelectorModal(true)
    } else {
      setSelectedLegalEntity(selectedLegalEntity)
      navigate(
        navigation.employees.updaterContracts
          .fullPath({
            legal_entity_id: selectedLegalEntity,
          })
          .replace(/\*+$/, ''),
        {
          state: { selectedLegalEntity: selectedLegalEntity },
        }
      )
    }
  }

  const handleUpdaterLeModeConfirm = (result: UpdaterLeModeResult) => {
    setShowUpdaterLeModeModal(false)

    if (result.mode === 'single') {
      const routeKey = importType === 'contracts' ? 'updaterContracts' : 'updaterEmployees'
      navigate(
        navigation.employees[routeKey]
          .fullPath({
            legal_entity_id: result.legalEntityId,
          })
          .replace(/\*+$/, ''),
        {
          state: { selectedLegalEntity: result.legalEntityId },
        }
      )
    } else {
      // Multi-LE mode: navigate with 'all' as legal_entity_id sentinel, pass selectedFields in state
      const routeKey = importType === 'contracts' ? 'updaterContracts' : 'updaterEmployees'
      navigate(
        navigation.employees[routeKey]
          .fullPath({
            legal_entity_id: 'all',
          })
          .replace(/\*+$/, ''),
        {
          state: { selectedFields: result.selectedFields },
        }
      )
    }
  }

  const onClickImportContracts = () => {
    setImportType('contracts')
    if (sortedLegalEntities.length > 1) {
      setModalAction('import')
      setShowLegalEntitySelectorModal(true)
    } else {
      setSelectedLegalEntity(selectedLegalEntity)
      navigateToImportAgreements(selectedLegalEntity)
    }
  }

  const navigateToImportAgreements = (legalEntityId: string) => {
    navigate(
      navigation.employees.importAgreements
        .fullPath({
          legal_entity_id: legalEntityId,
        })
        .replace(/\*+$/, ''),
      {
        state: {
          selectedLegalEntity: legalEntityId,
        },
      }
    )
  }

  const navigateToImportContracts = (
    legalEntityId: string,
    employeesToImport: EmployeeCollectionListIdsEmployeeFragment[] = selectedEmployees
  ) => {
    const employeesInLegalEntity = employeesToImport
      .filter((employee) => employee.legalEntity?.id === legalEntityId)
      .map((employee) => employee.id)
    navigate(
      navigation.employees.importContracts
        .fullPath({
          legal_entity_id: legalEntityId,
        })
        .replace(/\*+$/, ''),
      {
        state: {
          employeesIds: employeesInLegalEntity,
          selectedLegalEntity: legalEntityId,
          contractMode: 'current',
        },
      }
    )
  }

  const handleOnCloseEditContractsModeModal = () => {
    setShowEditContractsModeModal(false)
  }

  const navigateToEditContracts = (mode: 'all' | 'current') => {
    if (hasFieldSelectionFeature) {
      setUnifiedEditionEmployees(
        selectedEmployees.map((emp) => ({
          id: emp.id,
          firstName: emp.firstName,
          lastName: emp.lastName,
          legalEntityId: emp.legalEntity?.id ?? null,
        }))
      )
      setUnifiedEditionTarget('contracts')
      setUnifiedEditionContractMode(mode)
      setShowUnifiedEditionFlow(true)
    } else {
      const employeesInLegalEntity = selectedEmployees
        .filter((employee) => (employee.legalEntity?.id ?? '0') === selectedLegalEntity)
        .map((employee) => employee.id)
      navigate(navigation.employees.editContracts.fullPath(), {
        state: {
          employeesIds: employeesInLegalEntity,
          contractMode: mode,
          selectedLegalEntity,
        },
      })
    }
  }

  const handleOnSelectEditContractsMode = (mode: 'all' | 'current' | 'new') => {
    setSelectedEditContractsMode(mode)
    setShowEditContractsModeModal(false)

    if (mode === 'new') {
      // "Create a new version" is an import flow — needs a single LE
      if (hasFieldSelectionFeature) {
        // FF enabled: LE was not pre-selected, need to show LE selector
        const selectedLegalEntityIdsFromSelectedEmployees = Array.from(
          new Set(selectedEmployees.map((employee) => employee.legalEntity?.id))
        )
        if (selectedLegalEntityIdsFromSelectedEmployees.length > 1) {
          setModalAction('new_version')
          setImportType('contracts')
          setShowLegalEntitySelectorModal(true)
        } else {
          const legalEntityId =
            selectedLegalEntityIdsFromSelectedEmployees[0]?.toString() ?? selectedLegalEntity
          navigateToImportContracts(legalEntityId, selectedEmployees)
        }
      } else {
        // FF disabled: LE was already selected in the first modal
        navigateToImportContracts(selectedLegalEntity, selectedEmployees)
      }
    } else {
      navigateToEditContracts(mode)
    }
  }

  const [
    createIntegrationsdatevlaudsEmployeeImport,
    { loading: loadingCreateDatevIntegrationEmployeeImport },
  ] = useMutation(CreateDatevLaudsEmployeeImportDocument, {
    extractorFn: (importData) =>
      importData.integrationsDatevLaudsMutations.createIntegrationsdatevlaudsEmployeeImport,
  })

  const [bulkCreateInvitation] = useMutation(BulkCreateAuthenticationInvitationDocument, {
    extractorFn: (mutationData) =>
      mutationData.authenticationMutations.bulkCreateAuthenticationInvitation,
  })

  const handleDatevLaudsEmployeesImport = async () => {
    const result = await createIntegrationsdatevlaudsEmployeeImport({
      variables: {
        companyId: company.id,
        accessId: access.id,
      },
    })

    const handled = handleResult(result, (_errors) => {})

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('employees.importer_selection.success_message'),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('employees.importer_selection.error_message'),
      })
    }
  }

  const handleInviteEmployee = async (
    employee: EmployeeCollectionListEmployeeFragment,
    toggleRefetch?: () => void
  ) => {
    const result = await bulkCreateInvitation({
      variables: {
        companyId: company.id,
        employeeIds: [employee.id],
      },
    })

    const handled = handleResult(result, () => {})

    if (handled.success) {
      toggleRefetch?.()
      app.setFlash({
        type: 'correct',
        message: i18n.t('employees.invitation_sent'),
      })
    }
  }

  const handleActivateEmployee = async (
    employee: EmployeeCollectionListEmployeeFragment,
    toggleRefetch: () => void
  ) => {
    const { data: unterminatedEmployee, error: unterminateError } = await unterminate({
      id: employee.id,
    })

    if (unterminatedEmployee?.employee?.id) {
      toggleRefetch()
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
        time: 2000,
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: unterminateError || i18n.t('common.save_error'),
        time: 2000,
      })
    }
  }

  const handleRequestBulkFDContracts = (
    selEmployees: EmployeeCollectionListIdsEmployeeFragment[]
  ) => {
    setShowFDContractsConfirm(true)
    setSelectedEmployees(selEmployees)
  }

  return (
    <ResponsivePage.Body
      showHeader={canInvite}
      header={{
        widget: <Header />,
      }}
      height="none"
      loading={loading || loadingCreateDatevIntegrationEmployeeImport}
      error={error}
    >
      {showBecomeModal && !!employeeToBecome?.access.id && (
        <BecomeModal
          accessId={employeeToBecome?.access.id}
          firstName={employeeToBecome?.firstName ?? ''}
          onClose={() => {
            setShowBecomeModal(false)
          }}
        />
      )}
      {showUnifiedEditionFlow && (
        <UnifiedEditionFlow
          companyId={company.id}
          employees={unifiedEditionEmployees}
          importType={
            unifiedEditionTarget === 'contracts' ? 'contract_version_edition' : 'employee_edition'
          }
          onClose={() => setShowUnifiedEditionFlow(false)}
          onConfirm={handleUnifiedEditionConfirm}
        />
      )}
      {showUpdaterLeModeModal && (
        <UpdaterLeModeModal
          isOpen={showUpdaterLeModeModal}
          onClose={() => setShowUpdaterLeModeModal(false)}
          onConfirm={handleUpdaterLeModeConfirm}
          legalEntities={sortedLegalEntities.map((le) => ({
            id: le.id.toString(),
            legalName: le.legalName,
          }))}
          companyId={company.id}
          importType={importType === 'contracts' ? 'contract_version_edition' : 'employee_edition'}
        />
      )}
      {showLegalEntitySelectorModal && (
        <F0Dialog
          isOpen={showLegalEntitySelectorModal}
          onClose={handleOnCloseModal}
          dataTestId="legal-entity-selector-modal"
          width="sm"
          title={
            modalAction === 'edit' && importType === 'contracts'
              ? i18n.t('importer_employees.legal_entity_modal.title.edit_contracts')
              : modalAction === 'import' && importType === 'contracts'
                ? i18n.t('importer_employees.legal_entity_modal.title.import_contracts')
                : modalAction === 'edit'
                  ? i18n.t('importer_employees.legal_entity_modal.title.edit')
                  : i18n.t('importer_employees.legal_entity_modal.title.import')
          }
          description={
            modalAction === 'edit' && importType === 'contracts'
              ? i18n.t('importer_employees.legal_entity_modal.description.edit_contracts')
              : modalAction === 'import' && importType === 'contracts'
                ? i18n.t('importer_employees.legal_entity_modal.description.import_contracts')
                : modalAction === 'edit'
                  ? i18n.t('importer_employees.legal_entity_modal.description.edit')
                  : i18n.t('importer_employees.legal_entity_modal.description.import')
          }
          primaryAction={{
            label: i18n.t('common.next'),
            onClick: handleOnClickNextModal,
            disabled: !selectedLegalEntity,
          }}
          secondaryAction={{
            label: i18n.t('common.cancel'),
            onClick: handleOnCloseModal,
          }}
        >
          <CardSelectableContainer<string>
            value={selectedLegalEntity}
            onChange={(value) => {
              if (value) setSelectedLegalEntity(value)
            }}
            label={i18n.t('importer_employees.legal_entity_modal.sub_title')}
            items={legalEntityOptions.map<CardSelectableItem<string>>((option) => {
              const employeesInLegalEntity = selectedEmployees.filter(
                (employee) => (employee.legalEntity?.id?.toString() ?? '0') === String(option.value)
              )

              return {
                value: option.value,
                title: option.label,
                description:
                  employeesInLegalEntity.length > 0
                    ? i18n.t('importer_employees.legal_entity_modal.employee_count', {
                        count: employeesInLegalEntity.length,
                      })
                    : undefined,
              }
            })}
          />
        </F0Dialog>
      )}
      {showEditContractsModeModal && (
        <F0Dialog
          isOpen={showEditContractsModeModal}
          onClose={handleOnCloseEditContractsModeModal}
          title={i18n.t('edit_agreement.mode_selector.title')}
          description={i18n.t('edit_agreement.mode_selector.description')}
          primaryAction={{
            label: i18n.t('common.continue'),
            onClick: () => handleOnSelectEditContractsMode(selectedEditContractsMode),
          }}
          secondaryAction={{
            label: i18n.t('common.cancel'),
            onClick: handleOnCloseEditContractsModeModal,
          }}
        >
          <Box flexDirection="column" gap="s16">
            <CardSelectableContainer
              label={i18n.t('edit_agreement.mode_selector.title')}
              value={selectedEditContractsMode}
              onChange={(value) => {
                if (value) setSelectedEditContractsMode(value)
              }}
              items={[
                {
                  value: 'current',
                  title: i18n.t('edit_agreement.mode_selector.edit_current.name'),
                  description: i18n.t('edit_agreement.mode_selector.edit_current.description'),
                },
                {
                  value: 'all',
                  title: i18n.t('edit_agreement.mode_selector.edit_all.name'),
                  description: i18n.t('edit_agreement.mode_selector.edit_all.description'),
                },
                {
                  value: 'new',
                  title: i18n.t('edit_agreement.mode_selector.new_from_current.name'),
                  description: i18n.t('edit_agreement.mode_selector.new_from_current.description'),
                },
              ]}
            />

            <F0Alert
              variant="info"
              title={i18n.t('edit_agreement.mode_selector.info_header')}
              description={i18n.t('edit_agreement.mode_selector.info')}
            />
          </Box>
        </F0Dialog>
      )}
      {showTerminateConfirm && (
        <BulkTerminateModal
          selectedEmployeeIds={selectedForTermination}
          eligibleEmployeeIds={eligibleForTermination}
          terminatedSelectedCount={terminatedSelectedCount}
          onCancel={() => setShowTerminateConfirm(false)}
          onConfirm={(ids) => {
            setShowTerminateConfirm(false)
            navigate(navigation.employees.terminateBulk.fullPath(), {
              state: {
                employeesIds: ids,
              },
            })
          }}
        />
      )}
      {showTerminationImporterModal && (
        <TerminationImporterModal
          employeeIds={[]}
          onClose={() => setShowTerminationImporterModal(false)}
        />
      )}
      {showPauseConfirm && (
        <BulkPauseModal
          selectedCount={pauseSelectedCount}
          eligibleCount={pauseEligibleIds.length}
          onCancel={() => setShowPauseConfirm(false)}
          onConfirm={() => {
            setShowPauseConfirm(false)
            navigate(navigation.employees.fijosPauseBulk.fullPath(), {
              state: { employeesIds: pauseEligibleIds },
            })
          }}
        />
      )}
      {showActivateConfirm && (
        <BulkActivateModal
          selectedCount={activateSelectedCount}
          eligibleCount={activateEligibleIds.length}
          onCancel={() => setShowActivateConfirm(false)}
          onConfirm={() => {
            setShowActivateConfirm(false)
            navigate(navigation.employees.fijosActivateBulk.fullPath(), {
              state: { employeesIds: activateEligibleIds },
            })
          }}
        />
      )}
      {showFDContractsConfirm && (
        <BulkFDContractsModal
          onCancel={() => setShowFDContractsConfirm(false)}
          onConfirm={() => {
            setShowFDContractsConfirm(false)
            navigate(navigation.employees.fdContractBulk.fullPath(), {
              state: { employeesIds: selectedEmployees.map((employee) => employee.id) },
            })
          }}
        />
      )}
      {additionalCompensationEmployeeIds.length > 0 && (
        <BulkCreateAdditionalCompensationsWizardModal
          employeeIds={additionalCompensationEmployeeIds}
          onClose={() => setAdditionalCompensationEmployeeIds([])}
          onSuccess={() => setAdditionalCompensationEmployeeIds([])}
        />
      )}
      <EmployeeCollection
        canCreateEmployee={canCreateEmployee}
        canImportEmployees={canImportEmployees}
        canEditEmployeesInBulk={canEditEmployeesInBulk}
        canImportResources={canImportResources}
        canExportTimeoff={canExportTimeoff}
        canExportEmployees={canExportEmployees}
        canImportTimeoffAllowances={canImportTimeoffAllowances}
        canEditContracts={canEditContracts}
        canBulkImportCustomTables={!!canBulkImportCustomTables}
        canSeeFormerEmployees={canSeeFormerEmployees}
        canSeeJobTitle={canSeeJobTitle}
        canSeeJobCatalogTree={canSeeJobCatalogTree}
        canSeeJobCatalog={canSeeJobCatalog}
        hasLegacyJobCatalog={hasLegacyJobCatalog}
        canSeePendingPromotions={canSeePendingPromotions}
        canSeeStartDate={canSeeStartDate}
        canSeeEndDate={canSeeEndDate}
        hasCompensationsModule={hasCompensationsManagementFeature}
        hasPayrollManagementModule={hasPayrollManagementFeature}
        hasDatevIntegration={hasDatevIntegration}
        hasDevUniversalContractsEditionFeature={hasDevUniversalContractsEditionFeature}
        hasDevUniversalContractsImportFeature={hasDevUniversalContractsImportFeature}
        hasDevFixedDiscontinuousContractsFeature={hasDevFixedDiscontinuousContractsFeature}
        hasDevUniversalUpdaterEmployeesFeature={hasDevUniversalUpdaterEmployeesFeature}
        hasBecomeEnabledFeature={hasBecomeEnabledFeature}
        hasFulltextEmployeeSearch={hasFulltextEmployeeSearch}
        handleClickEditPeopleFromFile={onClickEditPeopleFromFile}
        handleClickEditAgreementsFromFile={onClickEditAgreementsFromFile}
        handleClickImportPeople={onClickImportPeople}
        handleClickImportContracts={onClickImportContracts}
        handleDatevLaudsEmployeesImport={handleDatevLaudsEmployeesImport}
        handleClickEmployeesBulkEdition={handleClickEmployeesBulkEdition}
        handleClickContractsBulkEdition={handleClickContractsBulkEdition}
        handleClickAdditionalCompensationsBulkEdition={
          handleClickAdditionalCompensationsBulkEdition
        }
        handleClickSupplementsBulkEdition={handleClickSupplementsBulkEdition}
        handleBecome={handleBecome}
        handleInviteEmployee={handleInviteEmployee}
        handleActivateEmployee={handleActivateEmployee}
        handleRequestBulkTerminate={handleRequestBulkTerminate}
        handleRequestBulkPause={handleRequestBulkPause}
        handleRequestBulkActivate={handleRequestBulkActivate}
        handleRequestBulkFDContracts={handleRequestBulkFDContracts}
      />
      <Outlet />
    </ResponsivePage.Body>
  )
}

export default EmployeeCollectionPage
